import React from 'react';
import { Sparkles, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Confirmation modal shown before any LLM-spawning regenerate action.
 */
export default function RegenerateConfirmModal({
  open,
  onClose,
  onConfirm,
  scope = 'full',           // "full" or "stage"
  stageLabel = '',
  busy = false,
  cascade = false,          // when true, shows the Stage-1-needed cascade copy
}) {
  if (!open) return null;

  // M3.14: Reverted to Pro + full scene_text input. Worst-case per stage
  // ~8K-12K input × $1.25/M + 8K output × $5.00/M ≈ $0.05-0.06.
  // Full 4-stage parallel run ≈ $0.20-0.25, plus optional Stage-1 cascade ~$0.10.
  // Round up for honest user disclosure.
  const FULL_COST = '0.65';
  const STAGE_COST = '0.11';
  const cost = scope === 'full' ? FULL_COST : STAGE_COST;

  const title = cascade
    ? 'Story Understanding required'
    : scope === 'full'
      ? 'Regenerate beat board'
      : `Regenerate ${stageLabel}`;

  const body = cascade ? (
    <>
      Beat extraction needs the Story Understanding (Stage 1) cache.
      <br /><br />
      <span style={{ color: 'var(--cb-text-sec)' }}>
        Confirm to generate it now (~$0.05) and then automatically run the beat
        board regenerate.
      </span>
    </>
  ) : (
    <>
      This will use <span style={{ color: 'var(--cb-accent)' }} className="font-semibold">FiPr AI</span> to
      re-analyze your screenplay.
      <br /><br />
      <span style={{ color: 'var(--cb-text-sec)' }}>
        Estimated cost: <span className="font-mono font-bold" style={{ color: 'var(--cb-text)' }}>~${cost}</span>
      </span>
    </>
  );

  return (
    <div
      data-testid="regen-confirm-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl border max-w-md w-full p-6"
        style={{ background: 'var(--cb-surface)', borderColor: 'var(--cb-border)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          {cascade
            ? <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#F5A623' }} />
            : <Sparkles className="h-5 w-5 shrink-0 mt-0.5" style={{ color: 'var(--cb-accent)' }} />}
          <h2 className="text-lg font-bold leading-tight" style={{ color: 'var(--cb-text)' }}>
            {title}
          </h2>
        </div>

        <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--cb-text)' }}>
          {body}
        </p>

        <div className="flex justify-end gap-2">
          <Button
            data-testid="regen-cancel-btn"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            className="text-sm"
          >
            Cancel
          </Button>
          <Button
            data-testid="regen-confirm-btn"
            onClick={onConfirm}
            disabled={busy}
            className="font-semibold"
            style={{ background: 'var(--cb-accent)', color: '#000' }}
          >
            {busy ? 'Working…' : (cascade ? 'Continue' : 'Confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}
